// client/src/pages/createProfile.jsx

import React, { useState, useContext, useEffect } from 'react';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../context/provider';
import authAction from "../../context/actions/authAction";

import useMessage from '../../hooks/useMessage';
import { messaging } from '../../utilities/messaging/firebaseConfig'
import { getToken } from 'firebase/messaging';

import useStudent from '../../hooks/useStudent';
import useInstructor from '../../hooks/useInstructor';
import useRecognizedInstructor from '../../hooks/useRecognizedInstructor';

import '../../styles/globalstyles.css';
import './createProfile.css';

const CreateProfile = () => {
  const { createStudent } = useStudent();
  const { createInstructor } = useInstructor();
  const { isRecognizedInstructor } = useRecognizedInstructor();


  const navigate = useNavigate();
  const { 
    authState: { authData },
    authDispatch,
  } = useContext(GlobalContext);

  const [formData, setFormData] = useState({
    phone: '',
    dateOfBirth: '',
    gender: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [willBeInstructor, setWillBeInstructor] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [profileType, setProfileType] = useState('student');

  const { registerToken } = useMessage();

  // ✅ ADD: Redirect if not authenticated
  useEffect(() => {
    if (!authData || !authData.id) {
      console.log('❌ Not authenticated - redirecting to login');
      navigate('/');
    }
  }, [authData, navigate]);

  useEffect(() => {
    const checkRole = async () => {
      try {
        const isInstructor = await isRecognizedInstructor(authData?.email);
        setWillBeInstructor(isInstructor);
      } catch (error) {
        console.error('Error checking role:', error);
        setWillBeInstructor(false);
      } 
      // finally {
      //   setCheckingRole(false);
      // }
    };
    
    if (authData?.email) {
      checkRole();
    }
  }, [authData?.email]);

  const validatePhone = (phone) => {
    const phoneRegex = /^[\d\s\-\(\)]+$/;
    if (!phone) return 'Phone number is required';
    if (phone.length < 10) return 'Phone number must be at least 10 digits';
    if (!phoneRegex.test(phone)) return 'Invalid phone number format';
    return null;
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return 'Date of birth is required';
    
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dob.match(dateRegex);
    
    if (!match) return 'Format must be DD/MM/YYYY';
    
    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const year = parseInt(match[3]);
    
    if (month < 1 || month > 12) return 'Invalid month';
    if (day < 1 || day > 31) return 'Invalid day';
    
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;
    if (age < 13) return 'Must be at least 13 years old';
    if (age > 120) return 'Invalid year';
    
    const date = new Date(year, month - 1, day);
    if (date.getDate() !== day) return 'Invalid date';
    
    return null;
  };

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.className = 'notification-toast';
    notification.style.background = type === 'success' ? 
      'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 
      'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    notification.innerHTML = `
      <span class="notification-icon">
        ${type === 'success' ? '✅' : '❌'}
      </span>
      <span>${message}</span>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out forwards';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      
      if (cleaned.length >= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      } else if (cleaned.length >= 3) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    }
    else if (name === 'dateOfBirth') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      
      if (cleaned.length >= 4) {
        formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
      } else if (cleaned.length >= 2) {
        formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setErrors(prev => ({ ...prev, [name]: null }));
  };

const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    const newErrors = {};
    
    // Common validations
    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;
    
    const dobError = validateDateOfBirth(formData.dateOfBirth);
    if (dobError) newErrors.dateOfBirth = dobError;
    
    // Instructor-specific validations
    if (willBeInstructor) {
      if (!formData.title?.trim()) {
        newErrors.title = 'Title is required for instructors';
      }
      if (!formData.department?.trim()) {
        newErrors.department = 'Department is required for instructors';
      }
      if (!formData.bio?.trim()) {
        newErrors.bio = 'Bio is required for instructors';
      } else if (formData.bio.length < 50) {
        newErrors.bio = 'Bio must be at least 50 characters';
      } else if (formData.bio.length > 1000) {
        newErrors.bio = 'Bio must not exceed 1000 characters';
      }
      if (!formData.expertise?.trim()) {
        newErrors.expertise = 'At least one area of expertise is required';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const basePayload = {
        loginId: authData?.id,
        name: authData?.name,
        email: authData?.email,
        imageUrl: authData?.photo,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender || '',
        isActive: true,
        accountStatus: 'active',
        consents: {
          dataCollection: true,
          dataSharing: true,
          analyticsTracking: true,
          dataProcessingOutsideCanada: true,
          privacyPolicy: true,
          consentDate: new Date().toISOString(),
          consentVersion: '1.0'
        }
      };
      if (willBeInstructor) {
        const instructorPayload = {
          ...basePayload,
          title: formData.title.trim(),
          department: formData.department.trim(),
          bio: formData.bio.trim(),
          expertise: formData.expertise.split(',').map(e => e.trim()).filter(e => e),
          qualifications: formData.qualifications ? 
            formData.qualifications.split(',').map(q => q.trim()).filter(q => q) : [],
          officeLocation: formData.officeLocation?.trim() || '',
          officeHours: formData.officeHours?.trim() || '',
          assignedCourses: [],
          gradingStats: {
            totalGraded: 0,
            labsGraded: 0,
            quizzesGraded: 0,
            examsGraded: 0,
            capstonesGraded: 0
          }
        };

        const response = await createInstructor(authData?.id, instructorPayload);
        
        if (response.success) {
          console.log('✅ Instructor Profile created successfully');
      
          // ✅ Register FCM token after profile is created
          try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
              const fcmToken = await getToken(messaging, { 
                vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY 
              });
              await registerToken(fcmToken);
              console.log('✅ FCM registered after profile creation');
            }
          } catch (error) {
            console.error('FCM registration failed:', error);
          }

          localStorage.setItem('userRole', 'instructor');
          authAction('UPDATE', { ...authData, role: 'instructor' })(authDispatch);
          setProfileType('instructor');
          setShowSuccessModal(true);
          // showNotification('Instructor Profile created successfully!', 'success');
          setTimeout(() => {
            navigate('/instructorDashboard');
          }, 2000);
        } else {
          throw new Error('Failed to create instructor profile');
        }
      }
      else {
        const studentPayload = {
          ...basePayload,
          courses: [],
          progress: [],
          labProgress: [],
          quizProgress: [],
          examProgress: [],
          capstoneProjects: [],
          courseGrades: [],
          badges: [],
          cart: [],
          paymentHistory: []
        };

        const response = await createStudent(authData?.id, studentPayload);

        if (response.success) {
          console.log('✅ Student Profile created successfully');
      
          // ✅ Register FCM token after profile is created
          try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
              const fcmToken = await getToken(messaging, { 
                vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY 
              });
              await registerToken(fcmToken);
              console.log('✅ FCM registered after profile creation');
            }
          } catch (error) {
            console.error('FCM registration failed:', error);
          }

          localStorage.setItem('userRole', 'student');
          authAction('UPDATE', { ...authData, role: 'student' })(authDispatch);
          setProfileType('student');
          setShowSuccessModal(true);
          // showNotification('Student Profile created successfully!', 'success');
          setTimeout(() => {
            navigate('/studentDashboard');
          }, 2000);
        } else {
          throw new Error('Failed to create student profile');
        }
      }
    } catch (error) {
      console.error('Profile creation failed:', error);
      showNotification(error.response?.data?.error || 'Failed to create profile', 'error');
      setErrors({ submit: error.message || 'Failed to create profile. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="main-container">
      <div className="main-content">
        
        <div className="header-row">
          <ProfileHeader data={authData} activePage="create-profile" />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>
        
        <div className="page-title">
          <h2>✨ Create Your {willBeInstructor ? 'Instructor' : 'Student'} Profile</h2>
          <p className="page-subtitle">
            Let's get started with your information
          </p>
        </div>

        <div className="scrollable-list">
          
          <div className="createprofile-card">
            <div className="createprofile-header">
              {authData?.photo ? (
                <img 
                  src={authData.photo} 
                  alt={authData?.name} 
                  className="createprofile-avatar"
                />
              ) : (
                <div className="createprofile-avatar-placeholder">
                  {authData?.name?.charAt(0) || 'U'}
                </div>
              )}

              <h3 className="createprofile-name">{authData?.name}</h3>
              <p className="expertise">{authData?.email}</p>
              
              {willBeInstructor && (
                <div className="createprofile-badge">
                  🎓 Recognized Instructor
                </div>
              )}
            </div>

            <div className="createprofile-form">
              
              <div className="createprofile-section">
                <h3 className="createprofile-section-title">
                  📋 Basic Information
                </h3>

                {/* Phone number */}
                <div className="createprofile-field">
                  <label className="createprofile-label">
                    📱 Phone Number <span className="createprofile-required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(123) 456-7890"
                    maxLength={14}
                    className={`dropdown ${errors.phone ? 'createprofile-input-error' : ''}`}
                  />
                  {errors.phone && (
                    <span className="createprofile-error-message">
                      ⚠️ {errors.phone}
                    </span>
                  )}
                </div>
                {/* DOB */}
                <div className="createprofile-field">
                  <label className="createprofile-label">
                    📅 Date of Birth <span className="createprofile-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    className={`dropdown ${errors.dateOfBirth ? 'createprofile-input-error' : ''}`}
                  />
                  {errors.dateOfBirth && (
                    <span className="createprofile-error-message">
                      ⚠️ {errors.dateOfBirth}
                    </span>
                  )}
                </div>
                {/* Gender */}
                <div className="createprofile-field">
                  <label className="createprofile-label">
                    👤 Gender (Optional)
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="dropdown"
                  >
                    <option value="">Select gender...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>  

              {willBeInstructor && (
                <div className="createprofile-section">
                  <h3 className="createprofile-section-title">
                    🎓 Professional Information
                  </h3>
                  {/* title */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      👔 Title <span className="createprofile-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g., Professor, Dr., Lecturer"
                      className={`dropdown ${errors.title ? 'createprofile-input-error' : ''}`}
                    />
                    {errors.title && (
                      <span className="createprofile-error-message">
                        ⚠️ {errors.title}
                      </span>
                    )}
                  </div>
                  {/* department */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      🏢 Department <span className="createprofile-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      placeholder="e.g., Computer Science, Engineering"
                      className={`dropdown ${errors.department ? 'createprofile-input-error' : ''}`}
                    />
                    {errors.department && (
                      <span className="createprofile-error-message">
                        ⚠️ {errors.department}
                      </span>
                    )}
                  </div>
                  {/* biography */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      📝 Bio <span className="createprofile-required">*</span>
                      <span className="createprofile-char-counter">
                        ({formData?.bio?.length}/1000 characters, min 50)
                      </span>
                    </label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Tell us about yourself, your teaching philosophy, experience, and what makes you passionate about education..."
                      rows={4}
                      maxLength={1000}
                      className={`dropdown createprofile-textarea ${errors.bio ? 'createprofile-input-error' : ''}`}
                    />
                    {errors.bio && (
                      <span className="createprofile-error-message">
                        ⚠️ {errors.bio}
                      </span>
                    )}
                  </div>
                  {/* expertise */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      💡 Areas of Expertise <span className="createprofile-required">*</span>
                      <span className="createprofile-helper-text">
                        (comma-separated)
                      </span>
                    </label>
                    <input
                      type="text"
                      name="expertise"
                      value={formData.expertise}
                      onChange={handleChange}
                      placeholder="e.g., IoT, Embedded Systems, Cloud Computing"
                      className={`dropdown ${errors.expertise ? 'createprofile-input-error' : ''}`}
                    />
                    {errors.expertise && (
                      <span className="createprofile-error-message">
                        ⚠️ {errors.expertise}
                      </span>
                    )}
                  </div>
                  {/* qualifications */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      🎯 Qualifications (Optional)
                      <span className="createprofile-helper-text">
                        (comma-separated)
                      </span>
                    </label>
                    <input
                      type="text"
                      name="qualifications"
                      value={formData.qualifications}
                      onChange={handleChange}
                      placeholder="e.g., PhD in Computer Science, 10 years industry experience"
                      className="dropdown"
                    />
                  </div>
                  {/* office location */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      📍 Office Location (Optional)
                    </label>
                    <input
                      type="text"
                      name="officeLocation"
                      value={formData.officeLocation}
                      onChange={handleChange}
                      placeholder="e.g., Building A, Room 305"
                      className="dropdown"
                    />
                  </div>
                  {/* office hours */}
                  <div className="createprofile-field">
                    <label className="createprofile-label">
                      🕐 Office Hours (Optional)
                    </label>
                    <input
                      type="text"
                      name="officeHours"
                      value={formData.officeHours}
                      onChange={handleChange}
                      placeholder="e.g., Mon-Wed 2-4 PM, Fri 10-12 PM"
                      className="dropdown"
                    />
                  </div>
                </div>
              )}

              {errors.submit && (
                <div className="createprofile-error-alert">
                  <span className="createprofile-error-icon">❌</span>
                  <span>{errors.submit}</span>
                </div>
              )}

              <div className="action-buttons">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="general-btn primary-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="loader createprofile-loader"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">✅</span>
                      Create Profile
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate('/usergate')}
                  disabled={isSubmitting}
                  className="general-btn secondary-btn"
                >
                  <span className="btn-icon">↩️</span>
                  Back
                </button>
              </div>
              {/* consents */}
              <div className="createprofile-security-notice">
                <span className="createprofile-security-icon">🔒</span>
                <div className="createprofile-security-text">
                  <p className="createprofile-security-title">Privacy & Data Consent</p>
                  <p className="createprofile-security-description">
                    By creating a profile, you consent to: data collection for platform purposes, 
                    data sharing with instructors/students as needed, analytics tracking for improving 
                    the platform, and data processing outside Canada. Your information is secure and 
                    will comply with our privacy policy (v1.0).
                  </p>
                </div>
              </div>

            </div>
          </div>

        </div> 

        {showSuccessModal && (
          <div className="createprofile-modal-overlay">
            <div className="createprofile-modal-content">
              <div className="createprofile-success-icon">
                ✓
              </div>
              <h3 className="createprofile-modal-title">
                Profile Created!
              </h3>
              <p className="createprofile-modal-message">
                Your {profileType} profile has been successfully created.
                Redirecting to your dashboard...
              </p>
              <div className="createprofile-progress-container">
                <div className="createprofile-progress-bar" />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );

};

export default CreateProfile;